import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import RouteGuard from '@/components/RouteGuard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { LogOut, Building, Package, Calendar, Star, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

interface Service {
  id: string;
  service_name: string;
  description: string;
  price: number;
  category: string;
  is_active: boolean;
  average_rating: number;
  total_ratings: number;
}

interface Booking {
  id: string;
  customer_name: string;
  customer_phone: string;
  property_address: string;
  booking_date: string;
  booking_time: string;
  notes: string;
  status: string;
  created_at: string;
  services: {
    service_name: string;
  };
}

interface ServiceRating {
  id: string;
  rating: number;
  comment: string;
  created_at: string;
  service_bookings: {
    customer_name: string;
  };
}

const ServiceProviderDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState<any>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [ratings, setRatings] = useState<ServiceRating[]>([]);
  
  const [newService, setNewService] = useState({
    service_name: '',
    description: '',
    price: '',
    category: ''
  });
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');

  useEffect(() => {
    fetchProviderData();
  }, []);

  const fetchProviderData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get provider info
      const { data: providerData } = await supabase
        .from('service_providers')
        .select('*')
        .eq('user_id', user.id)
        .single();

      setProvider(providerData);

      if (!providerData) return;

      // Get services
      const { data: servicesData } = await supabase
        .from('services')
        .select('*')
        .eq('provider_id', providerData.id)
        .order('created_at', { ascending: false });

      setServices(servicesData || []);

      // Get bookings
      const { data: bookingsData } = await supabase
        .from('service_bookings')
        .select(`
          *,
          services (service_name)
        `)
        .in('service_id', (servicesData || []).map(s => s.id))
        .order('created_at', { ascending: false });

      setBookings(bookingsData || []);

      // Get ratings
      const { data: ratingsData } = await supabase
        .from('service_ratings')
        .select(`
          *,
          service_bookings (customer_name)
        `)
        .in('service_id', (servicesData || []).map(s => s.id))
        .order('created_at', { ascending: false });

      setRatings(ratingsData || []);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile || !provider) return null;

    const fileExt = logoFile.name.split('.').pop();
    const fileName = `${provider.id}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('site-logos')
      .upload(filePath, logoFile, { upsert: true });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('site-logos')
      .getPublicUrl(filePath);

    // Update provider logo
    await supabase
      .from('service_providers')
      .update({ company_logo_url: publicUrl })
      .eq('id', provider.id);

    return publicUrl;
  };

  const handleUpdateLogo = async () => {
    if (!logoFile || !provider) return;

    try {
      setLoading(true);

      const newLogoUrl = await uploadLogo();

      if (newLogoUrl) {
        toast({
          title: "تم تحديث الشعار بنجاح",
          description: "تم حفظ الشعار الجديد لمؤسستك",
        });

        setLogoFile(null);
        setLogoPreview('');
        fetchProviderData();
      }
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!provider) return;

    // التحقق من الاشتراك النشط
    if (!provider.is_active || !provider.is_verified || 
        !provider.subscription_end_date || 
        new Date(provider.subscription_end_date) < new Date()) {
      toast({
        title: "الاشتراك غير مفعل",
        description: "يرجى التواصل مع الإدارة لتفعيل اشتراكك قبل إضافة الخدمات",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);

      // Upload logo if exists (only once per company)
      if (logoFile && !provider.company_logo_url) {
        await uploadLogo();
      }

      const { error } = await supabase
        .from('services')
        .insert({
          provider_id: provider.id,
          service_name: newService.service_name,
          description: newService.description,
          price: parseFloat(newService.price),
          category: newService.category,
          is_active: true
        });

      if (error) throw error;

      toast({
        title: "تم إضافة الخدمة بنجاح",
        description: "يمكن للعملاء الآن رؤية خدمتك",
      });

      setNewService({
        service_name: '',
        description: '',
        price: '',
        category: ''
      });
      setLogoFile(null);
      setLogoPreview('');

      fetchProviderData();
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditService = (service: Service) => {
    setEditingService(service);
    setIsEditing(true);
    setNewService({
      service_name: service.service_name,
      description: service.description,
      price: service.price.toString(),
      category: service.category
    });
  };

  const handleUpdateService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingService) return;

    try {
      setLoading(true);

      const { error } = await supabase
        .from('services')
        .update({
          service_name: newService.service_name,
          description: newService.description,
          price: parseFloat(newService.price),
          category: newService.category
        })
        .eq('id', editingService.id);

      if (error) throw error;

      toast({
        title: "تم تحديث الخدمة بنجاح",
        description: "تم حفظ التعديلات",
      });

      setNewService({
        service_name: '',
        description: '',
        price: '',
        category: ''
      });
      setIsEditing(false);
      setEditingService(null);

      fetchProviderData();
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذه الخدمة؟')) return;

    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', serviceId);

      if (error) throw error;

      toast({
        title: "تم حذف الخدمة",
        description: "تم حذف الخدمة بنجاح",
      });

      fetchProviderData();
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleToggleServiceStatus = async (serviceId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('services')
        .update({ is_active: !currentStatus })
        .eq('id', serviceId);

      if (error) throw error;

      toast({
        title: "تم تحديث حالة الخدمة",
        description: !currentStatus ? "تم تفعيل الخدمة" : "تم إيقاف الخدمة",
      });

      fetchProviderData();
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleUpdateBookingStatus = async (bookingId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('service_bookings')
        .update({ status })
        .eq('id', bookingId);

      if (error) throw error;

      toast({
        title: "تم تحديث حالة الحجز",
        description: `تم تغيير الحالة إلى: ${getStatusLabel(status)}`,
      });

      fetchProviderData();
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'قيد الانتظار',
      confirmed: 'مؤكد',
      completed: 'مكتمل',
      cancelled: 'ملغي'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  if (!provider) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">جاري التحميل...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {provider.company_logo_url ? (
                <img 
                  src={provider.company_logo_url} 
                  alt={provider.company_name}
                  className="h-12 w-12 object-contain rounded-lg border bg-white"
                />
              ) : (
                <Building className="h-8 w-8 text-primary" />
              )}
              <div>
                <h1 className="text-xl font-bold text-gray-900">{provider.company_name}</h1>
                <p className="text-sm text-gray-600">{provider.full_name}</p>
              </div>
            </div>
            <Button variant="ghost" onClick={handleLogout}>
              <LogOut className="ml-2 h-4 w-4" />
              تسجيل الخروج
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Company Logo Section */}
        <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              شعار المؤسسة
            </CardTitle>
            <CardDescription>
              قم بتحديث شعار مؤسستك الذي يظهر في جميع خدماتك
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Current Logo Display */}
              <div className="flex-shrink-0">
                {provider.company_logo_url ? (
                  <img 
                    src={provider.company_logo_url} 
                    alt={provider.company_name}
                    className="h-32 w-32 object-contain rounded-lg border-2 border-gray-200 bg-white p-3 shadow-md"
                  />
                ) : (
                  <div className="h-32 w-32 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                    <Building className="h-16 w-16 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Logo Upload Section */}
              <div className="flex-1 w-full space-y-4">
                {logoPreview && (
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <img 
                      src={logoPreview} 
                      alt="معاينة الشعار الجديد" 
                      className="h-20 w-20 object-contain border rounded-lg"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">الشعار الجديد</p>
                      <p className="text-xs text-muted-foreground">جاهز للرفع</p>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="logo-update">
                    {provider.company_logo_url ? 'اختر شعاراً جديداً' : 'رفع شعار المؤسسة'}
                  </Label>
                  <Input
                    id="logo-update"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground">
                    يُفضل استخدام صورة مربعة بدقة عالية (PNG أو JPG) - الحد الأقصى 5 ميجابايت
                  </p>
                </div>

                {logoFile && (
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleUpdateLogo}
                      disabled={loading}
                      className="flex items-center gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      {provider.company_logo_url ? 'تحديث الشعار' : 'رفع الشعار'}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview('');
                      }}
                      disabled={loading}
                    >
                      إلغاء
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Info */}
        {provider.subscription_end_date && (
          <Card className="mb-6 bg-gradient-to-r from-primary/10 to-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg mb-1">حالة الاشتراك</h3>
                  <p className="text-sm text-muted-foreground">
                    ينتهي في: {format(new Date(provider.subscription_end_date), 'dd MMMM yyyy', { locale: ar })}
                  </p>
                </div>
                <Badge variant={new Date(provider.subscription_end_date) > new Date() ? 'default' : 'destructive'}>
                  {new Date(provider.subscription_end_date) > new Date() ? 'نشط' : 'منتهي'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="services" dir="rtl" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="services">
              <Package className="ml-2 h-4 w-4" />
              خدماتي
            </TabsTrigger>
            <TabsTrigger value="bookings">
              <Calendar className="ml-2 h-4 w-4" />
              الحجوزات
            </TabsTrigger>
            <TabsTrigger value="ratings">
              <Star className="ml-2 h-4 w-4" />
              التقييمات
            </TabsTrigger>
          </TabsList>

          {/* Services Tab */}
          <TabsContent value="services" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{isEditing ? 'تعديل الخدمة' : 'إضافة خدمة جديدة'}</CardTitle>
                <CardDescription>
                  {isEditing ? 'قم بتعديل معلومات الخدمة' : 'أضف خدماتك ليتمكن العملاء من حجزها'}
                </CardDescription>
                {!isEditing && (!provider.is_active || !provider.is_verified || 
                  !provider.subscription_end_date || 
                  new Date(provider.subscription_end_date) < new Date()) && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                    <p className="text-red-800 text-sm text-right">
                      ⚠️ <strong>تنبيه:</strong> لا يوجد اشتراك نشط. لإضافة خدمات، يرجى التواصل مع الإدارة لتفعيل اشتراكك.
                    </p>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <form onSubmit={isEditing ? handleUpdateService : handleCreateService} className="space-y-4">
                  {/* Company Logo Upload - Only show if no logo exists and not editing */}
                  {!provider.company_logo_url && !isEditing && (
                    <div className="space-y-2">
                      <Label htmlFor="logo">شعار المؤسسة (اختياري)</Label>
                      <div className="flex items-center gap-4">
                        {logoPreview && (
                          <img 
                            src={logoPreview} 
                            alt="شعار المؤسسة" 
                            className="w-20 h-20 object-contain border rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <Input
                            id="logo"
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="cursor-pointer"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            الشعار سيُستخدم لجميع خدماتك - يُفضل استخدام صورة مربعة بدقة عالية
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="service_name">اسم الخدمة</Label>
                      <Input
                        id="service_name"
                        value={newService.service_name}
                        onChange={(e) => setNewService({ ...newService, service_name: e.target.value })}
                        placeholder="مثال: خدمة تنظيف منازل"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="price">السعر (ريال)</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        value={newService.price}
                        onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                        placeholder="100"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">التصنيف</Label>
                    <Select value={newService.category} onValueChange={(value) => setNewService({ ...newService, category: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="اختر التصنيف" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cleaning">تنظيف</SelectItem>
                        <SelectItem value="maintenance">صيانة</SelectItem>
                        <SelectItem value="ac">تكييف</SelectItem>
                        <SelectItem value="plumbing">سباكة</SelectItem>
                        <SelectItem value="electrical">كهرباء</SelectItem>
                        <SelectItem value="painting">دهان</SelectItem>
                        <SelectItem value="other">أخرى</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">وصف الخدمة</Label>
                    <Textarea
                      id="description"
                      value={newService.description}
                      onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                      placeholder="اكتب وصفاً تفصيلياً للخدمة..."
                      rows={4}
                      required
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      type="submit" 
                      disabled={loading || (!isEditing && (!provider.is_active || !provider.is_verified || 
                        !provider.subscription_end_date || 
                        new Date(provider.subscription_end_date) < new Date()))} 
                      className="flex-1"
                    >
                      {isEditing ? 'حفظ التعديلات' : (logoFile ? 'رفع الشعار وإضافة الخدمة' : 'إضافة الخدمة')}
                    </Button>
                    {isEditing && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setIsEditing(false);
                          setEditingService(null);
                          setNewService({
                            service_name: '',
                            description: '',
                            price: '',
                            category: ''
                          });
                        }}
                      >
                        إلغاء
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Services List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <Card 
                  key={service.id}
                  className="overflow-hidden transition-all duration-300 hover:shadow-2xl border border-gray-200 rounded-2xl bg-gradient-to-br from-white via-white to-gray-50 group"
                >
                  {/* صورة الشعار في header */}
                  <div className="relative h-40 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center overflow-hidden">
                    {provider.company_logo_url ? (
                      <img 
                        src={provider.company_logo_url} 
                        alt={provider.company_name}
                        className="h-24 w-24 object-contain rounded-lg bg-white p-3 shadow-lg transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <Building className="h-20 w-20 text-blue-300" />
                    )}
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent"></div>
                    
                    {/* الحالة في الأعلى */}
                    <div className="absolute top-3 right-3 z-10">
                      <Badge 
                        className={`font-medium py-1.5 px-3 rounded-full shadow-md ${
                          service.is_active 
                            ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                            : 'bg-gray-400 text-white'
                        }`}
                      >
                        {service.is_active ? 'نشط' : 'غير نشط'}
                      </Badge>
                    </div>

                    {/* السعر في الأسفل */}
                    <div className="absolute bottom-3 left-3 text-white z-10">
                      <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-1.5 rounded-lg shadow-lg">
                        {service.price} ر.ع
                      </div>
                    </div>
                  </div>

                  <CardContent className="p-5">
                    {/* اسم الخدمة واسم الشركة */}
                    <div className="mb-3">
                      <h3 className="font-bold text-xl text-gray-900 mb-1 text-right line-clamp-1 group-hover:text-blue-600 transition-colors">
                        {service.service_name}
                      </h3>
                      <div className="flex items-center gap-1 text-gray-600 text-sm">
                        <Building className="h-3.5 w-3.5 text-blue-500" />
                        <span>{provider.company_name}</span>
                      </div>
                    </div>

                    {/* التصنيف */}
                    <Badge className="mb-3 bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100">
                      {service.category}
                    </Badge>
                    
                    {/* الوصف */}
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3 text-right leading-relaxed">
                      {service.description}
                    </p>
                    
                    {/* التقييم */}
                    <div className="flex items-center gap-1 bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-2 mb-4 w-fit">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-bold text-gray-900">{service.average_rating || 0}</span>
                      <span className="text-sm text-gray-600">({service.total_ratings} تقييم)</span>
                    </div>

                    {/* الأزرار */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => handleEditService(service)}
                      >
                        تعديل
                      </Button>
                      <Button
                        size="sm"
                        variant={service.is_active ? 'outline' : 'default'}
                        className="w-full"
                        onClick={() => handleToggleServiceStatus(service.id, service.is_active)}
                      >
                        {service.is_active ? 'إيقاف' : 'تفعيل'}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="w-full col-span-2"
                        onClick={() => handleDeleteService(service.id)}
                      >
                        حذف الخدمة
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="space-y-4">
              {bookings.map((booking) => (
                <Card key={booking.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-lg mb-1">{booking.services.service_name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(booking.created_at), 'dd MMMM yyyy - HH:mm', { locale: ar })}
                        </p>
                      </div>
                      <Badge className={getStatusColor(booking.status)}>
                        {getStatusLabel(booking.status)}
                      </Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><strong>العميل:</strong> {booking.customer_name}</p>
                      <p><strong>رقم الهاتف:</strong> {booking.customer_phone}</p>
                      <p><strong>العنوان:</strong> {booking.property_address}</p>
                      <p><strong>التاريخ:</strong> {format(new Date(booking.booking_date), 'dd MMMM yyyy', { locale: ar })}</p>
                      <p><strong>الوقت:</strong> {booking.booking_time}</p>
                      {booking.notes && (
                        <p><strong>ملاحظات:</strong> {booking.notes}</p>
                      )}
                    </div>
                    {booking.status === 'pending' && (
                      <div className="flex gap-2 mt-4">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}
                          className="flex-1"
                        >
                          تأكيد الحجز
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                          className="flex-1"
                        >
                          إلغاء
                        </Button>
                      </div>
                    )}
                    {booking.status === 'confirmed' && (
                      <Button
                        size="sm"
                        onClick={() => handleUpdateBookingStatus(booking.id, 'completed')}
                        className="w-full mt-4"
                      >
                        تم الإنجاز
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
              {bookings.length === 0 && (
                <Card>
                  <CardContent className="pt-6 text-center text-muted-foreground">
                    لا توجد حجوزات حالياً
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Ratings Tab */}
          <TabsContent value="ratings">
            <div className="space-y-4">
              {ratings.map((rating) => (
                <Card key={rating.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold">{rating.service_bookings?.customer_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(rating.created_at), 'dd MMMM yyyy', { locale: ar })}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${
                              i < rating.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    {rating.comment && (
                      <p className="text-sm text-muted-foreground">{rating.comment}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
              {ratings.length === 0 && (
                <Card>
                  <CardContent className="pt-6 text-center text-muted-foreground">
                    لا توجد تقييمات حالياً
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const ServiceProviderDashboardPage = () => {
  return (
    <RouteGuard requiredUserType="service_provider">
      <ServiceProviderDashboard />
    </RouteGuard>
  );
};

export default ServiceProviderDashboardPage;
